import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import { useLabels, usePrefs } from 'wsm-common-data';
import RichContent from './richContent/RichContent';
import useFocusTrap from '../../hooks/modern/useFocusTrap';
import VehicleCards from './VehicleCards';
import SearchLinks from './SearchLinks';
import NoResults from './NoResults';
import RecentSearches from './RecentSearches';
import shouldShowRecentSearches from '../../util/modern/shouldShowRecentSearches';
import hasEmptyResults from '../../util/modern/hasEmptyResults';
import hasResults from '../../util/modern/hasResults';

const DropDown = ({
	dropDownRef = React.createRef(),
	inputRef,
	inputElementRef,
	isOpen,
	recentSearches,
	dropDownPosition,
	inputWidth,
	onRemove,
	handleClearHistory,
	onReorder,
	inventoryResults,
	pageResults,
	searchTerm,
	numberOfCharactersForFirstSearchNumber,
	handleChipClick,
	handleLinkClick,
	addInfo,
	richContentToDisplay,
	pageAlias,
	windowId,
	closeModal
}) => {
	const { suggestions = undefined, vehicles = undefined } =
		inventoryResults || {};
	const labels = useLabels();
	const prefs = usePrefs();
	const chipAriaLabel = labels.get('SEARCH');
	const removeAriaLabel = labels.get('REMOVE');
	const isSearchTermLongEnough =
		searchTerm?.length >= numberOfCharactersForFirstSearchNumber;

	const [responsiveClasses, setResponsiveClasses] = useState('col-sm-6');

	const [isSingleColumn, setIsSingleColumn] = useState(false);

	const hasResultsLocal = hasResults(
		pageResults,
		suggestions,
		vehicles,
		richContentToDisplay
	);

	const hasEmptyResultsLocal = hasEmptyResults(
		pageResults,
		suggestions,
		vehicles
	);

	const hasSingleResult =
		(vehicles?.length > 0 &&
			!suggestions?.length &&
			!pageResults?.length &&
			!richContentToDisplay.length) ||
		(suggestions?.length > 0 &&
			!vehicles?.length &&
			!pageResults?.length &&
			!richContentToDisplay.length) ||
		(pageResults?.length > 0 &&
			!suggestions?.length &&
			!vehicles?.length &&
			!richContentToDisplay.length) ||
		(richContentToDisplay.length > 0 &&
			!suggestions?.length &&
			!vehicles?.length &&
			!pageResults?.length);

	const hasVehiclesOrSearchLinks =
		vehicles?.length > 0 &&
		(pageResults?.length > 0 || suggestions?.length > 0);

	useFocusTrap(
		[inputRef, dropDownRef],
		inputElementRef,
		null,
		isOpen,
		closeModal
	);

	useEffect(() => {
		const newResponsiveClasses =
			inputWidth > 500 ? 'col-sm-6' : 'col-sm-12';
		setResponsiveClasses(newResponsiveClasses);
		setIsSingleColumn(newResponsiveClasses === 'col-sm-12');
	}, [inputWidth]);

	const classNames = [
		'modern-search-dropdown',
		'box',
		'p-4',
		!isOpen && 'hidden'
	];

	if (
		!recentSearches?.length &&
		(!searchTerm ||
			!isSearchTermLongEnough ||
			(pageResults === undefined &&
				suggestions === undefined &&
				vehicles === undefined))
	) {
		return null;
	}

	function renderLeftColumnContent() {
		if (hasSingleResult) {
			return (
				<>
					{vehicles?.length > 0 && (
						<VehicleCards
							vehicles={vehicles}
							handleLinkClick={handleLinkClick}
						/>
					)}
					{richContentToDisplay?.length > 0 && (
						<RichContent
							addInfo={addInfo}
							richContentToDisplay={richContentToDisplay}
							pageAlias={pageAlias}
							windowId={windowId}
							handleLinkClick={handleLinkClick}
						/>
					)}
					{suggestions?.length > 0 && (
						<SearchLinks
							type="inventory"
							showResults={prefs.showInventoryResults}
							data={suggestions}
							iconClass="ddc-icon-single-vehicle"
							headingLabel={labels.get(
								'INVENTORY_SEARCH_RESULTS'
							)}
							ariaLabel={labels.get('INVENTORY_SEARCH_RESULTS')}
							handleLinkClick={handleLinkClick}
						/>
					)}
					{pageResults?.length > 0 && (
						<SearchLinks
							type="page"
							showResults={prefs.showPageSearchResults}
							data={pageResults}
							iconClass="ddc-icon-arrow-circle-right"
							headingLabel={labels.get('PAGE_SEARCH_RESULTS')}
							ariaLabel={labels.get('PAGE_SEARCH_RESULTS')}
							handleLinkClick={handleLinkClick}
						/>
					)}
				</>
			);
		} else if (isSingleColumn) {
			return (
				<>
					{vehicles?.length > 0 && (
						<VehicleCards
							vehicles={vehicles}
							handleLinkClick={handleLinkClick}
						/>
					)}
					{suggestions?.length > 0 && (
						<SearchLinks
							type="inventory"
							showResults={prefs.showInventoryResults}
							data={suggestions}
							iconClass="ddc-icon-single-vehicle"
							headingLabel={labels.get(
								'INVENTORY_SEARCH_RESULTS'
							)}
							ariaLabel={labels.get('INVENTORY_SEARCH_RESULTS')}
							handleLinkClick={handleLinkClick}
						/>
					)}
					{pageResults?.length > 0 && (
						<SearchLinks
							type="page"
							showResults={prefs.showPageSearchResults}
							data={pageResults}
							iconClass="ddc-icon-arrow-circle-right"
							headingLabel={labels.get('PAGE_SEARCH_RESULTS')}
							ariaLabel={labels.get('PAGE_SEARCH_RESULTS')}
							handleLinkClick={handleLinkClick}
						/>
					)}
					{!hasVehiclesOrSearchLinks &&
						richContentToDisplay?.length > 0 && (
							<RichContent
								addInfo={addInfo}
								richContentToDisplay={richContentToDisplay}
								pageAlias={pageAlias}
								windowId={windowId}
								handleLinkClick={handleLinkClick}
							/>
						)}
				</>
			);
		} else {
			return (
				<>
					{vehicles?.length > 0 && (
						<VehicleCards
							vehicles={vehicles}
							handleLinkClick={handleLinkClick}
						/>
					)}
					{!vehicles?.length && suggestions?.length > 0 && (
						<SearchLinks
							type="inventory"
							showResults={prefs.showInventoryResults}
							data={suggestions}
							iconClass="ddc-icon-single-vehicle"
							headingLabel={labels.get(
								'INVENTORY_SEARCH_RESULTS'
							)}
							ariaLabel={labels.get('INVENTORY_SEARCH_RESULTS')}
							handleLinkClick={handleLinkClick}
						/>
					)}
					{!vehicles?.length && pageResults?.length > 0 && (
						<SearchLinks
							type="page"
							showResults={prefs.showPageSearchResults}
							data={pageResults}
							iconClass="ddc-icon-arrow-circle-right"
							headingLabel={labels.get('PAGE_SEARCH_RESULTS')}
							ariaLabel={labels.get('PAGE_SEARCH_RESULTS')}
							handleLinkClick={handleLinkClick}
						/>
					)}
				</>
			);
		}
	}

	function renderRightColumnContent() {
		return (
			<>
				{richContentToDisplay?.length > 0 &&
					!hasVehiclesOrSearchLinks && (
						<RichContent
							addInfo={addInfo}
							richContentToDisplay={richContentToDisplay}
							pageAlias={pageAlias}
							windowId={windowId}
							handleLinkClick={handleLinkClick}
						/>
					)}
				{suggestions?.length > 0 && vehicles?.length > 0 && (
					<SearchLinks
						type="inventory"
						showResults={prefs.showInventoryResults}
						data={suggestions}
						iconClass="ddc-icon-single-vehicle"
						headingLabel={labels.get('INVENTORY_SEARCH_RESULTS')}
						ariaLabel={labels.get('INVENTORY_SEARCH_RESULTS')}
						handleLinkClick={handleLinkClick}
					/>
				)}
				{pageResults?.length > 0 && vehicles?.length > 0 && (
					<SearchLinks
						type="page"
						showResults={prefs.showPageSearchResults}
						data={pageResults}
						iconClass="ddc-icon-arrow-circle-right"
						headingLabel={labels.get('PAGE_SEARCH_RESULTS')}
						ariaLabel={labels.get('PAGE_SEARCH_RESULTS')}
						handleLinkClick={handleLinkClick}
					/>
				)}
			</>
		);
	}

	const dropDownContent = (
		<div
			ref={dropDownRef}
			className={setClassNames(classNames)}
			data-testid="modern-input-dropdown"
			role="dialog"
			aria-labelledby={
				searchTerm ? 'results-heading' : 'recent-searches-heading'
			}
			aria-modal="true"
			style={{
				position: 'absolute',
				top: dropDownPosition.top,
				left: dropDownPosition.left,
				width: inputWidth
			}}
		>
			{isSearchTermLongEnough &&
				!hasResultsLocal &&
				hasEmptyResultsLocal && <NoResults searchTerm={searchTerm} />}

			{shouldShowRecentSearches(
				searchTerm,
				numberOfCharactersForFirstSearchNumber,
				hasResultsLocal,
				hasEmptyResultsLocal,
				recentSearches
			) && (
				<RecentSearches
					recentSearches={recentSearches}
					handleClearHistory={handleClearHistory}
					chipAriaLabel={chipAriaLabel}
					removeAriaLabel={removeAriaLabel}
					onRemove={onRemove}
					onReorder={onReorder}
					handleChipClick={handleChipClick}
				/>
			)}

			{isSearchTermLongEnough && hasResultsLocal && (
				<div>
					<h2 id="results-heading" className="sr-only">
						{labels.get('SEARCH_RESULTS')}
					</h2>
					<div className="row">
						<div
							className={responsiveClasses}
							data-testid="modern-input-column-one"
						>
							{renderLeftColumnContent()}
						</div>
						{!isSingleColumn && !hasSingleResult && (
							<div
								className={responsiveClasses}
								data-testid="modern-input-column-two"
							>
								{renderRightColumnContent()}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
	return ReactDOM.createPortal(dropDownContent, document.body);
};
DropDown.propTypes = {
	dropDownRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.shape() })
	]),
	inputRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.shape() })
	]),
	inputElementRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.shape() })
	]),
	isOpen: PropTypes.bool.isRequired,
	recentSearches: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string,
			id: PropTypes.string
		})
	),
	dropDownPosition: PropTypes.shape({
		top: PropTypes.number.isRequired,
		left: PropTypes.number.isRequired
	}).isRequired,
	inputWidth: PropTypes.number.isRequired,
	onRemove: PropTypes.func,
	handleClearHistory: PropTypes.func,
	onReorder: PropTypes.func,
	inventoryResults: PropTypes.shape({}),
	pageResults: PropTypes.arrayOf(PropTypes.shape({})),
	searchTerm: PropTypes.string,
	numberOfCharactersForFirstSearchNumber: PropTypes.number,
	handleChipClick: PropTypes.func,
	handleLinkClick: PropTypes.func,
	addInfo: PropTypes.shape(),
	richContentToDisplay: PropTypes.arrayOf(PropTypes.string),
	pageAlias: PropTypes.string,
	windowId: PropTypes.string,
	closeModal: PropTypes.func
};

export default DropDown;
